import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import inventoryService from '../services/inventoryService';
import StaffChat from '../components/communication/StaffChat';
import {
  Package, Archive, Warning, User, Envelope, Briefcase, ChatCircle
} from '@phosphor-icons/react';

function StorekeeperPortalPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [assetCount, setAssetCount] = useState(0);
  const [stockItemCount, setStockItemCount] = useState(0);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [tab, setTab] = useState('dashboard');

  useEffect(() => {
    if (user && user.role !== 'storekeeper') {
      toast.error('You do not have access to this page');
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!user?.school_id) return;

    Promise.all([
      inventoryService.listAssets(),
      inventoryService.listStockItems(),
    ])
      .then(([assets, stockItems]) => {
        setAssetCount(Array.isArray(assets) ? assets.length : 0);
        const items = Array.isArray(stockItems) ? stockItems : [];
        setStockItemCount(items.length);
        setLowStockItems(
          items.filter(i => i.reorder_level != null && i.quantity_on_hand <= i.reorder_level)
        );
      })
      .catch(err => {
        console.error(err);
        toast.error('Failed to load inventory overview');
      })
      .finally(() => setLoading(false));
  }, [user]);

  if (loading) {
    return (
      <Layout>
        <div className="page">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading storekeeper portal...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Inventory Portal</h1>
            <p>Assets, stock levels, and reorder alerts</p>
          </div>
          <div className="header-actions">
            <div className="user-info">
              <User size={20} />
              <span>{user?.first_name} {user?.last_name}</span>
            </div>
          </div>
        </div>

        {lowStockItems.length > 0 && (
          <div className="alert-banner alert-warning">
            <Warning size={24} weight="fill" />
            <div>
              <h3>{lowStockItems.length} stock item{lowStockItems.length > 1 ? 's' : ''} at or below reorder level</h3>
              <p>{lowStockItems.slice(0, 5).map(i => i.name).join(', ')}{lowStockItems.length > 5 ? '…' : ''}</p>
            </div>
          </div>
        )}

        <div className="tabs-container">
          <button className={`tab-btn ${tab === 'dashboard' ? 'active' : ''}`} onClick={() => setTab('dashboard')}>
            <Briefcase size={18} /> Dashboard
          </button>
          <button className={`tab-btn ${tab === 'messages' ? 'active' : ''}`} onClick={() => setTab('messages')}>
            <ChatCircle size={18} /> Messages
          </button>
        </div>

        {tab === 'messages' ? <StaffChat /> : (
        <>
        <div className="dashboard-grid">
          <div className="dashboard-card">
            <h3>My Profile</h3>
            <div className="profile-info">
              <div className="profile-item">
                <div className="profile-icon"><User size={20} /></div>
                <div className="profile-content">
                  <div className="profile-label">Name</div>
                  <div className="profile-value">{user?.first_name} {user?.last_name}</div>
                </div>
              </div>
              <div className="profile-item">
                <div className="profile-icon"><Envelope size={20} /></div>
                <div className="profile-content">
                  <div className="profile-label">Email</div>
                  <div className="profile-value">{user?.email}</div>
                </div>
              </div>
              <div className="profile-item">
                <div className="profile-icon"><Briefcase size={20} /></div>
                <div className="profile-content">
                  <div className="profile-label">Role</div>
                  <div className="profile-value">Storekeeper</div>
                </div>
              </div>
            </div>
          </div>

          <div className="dashboard-card">
            <h3>Inventory Overview</h3>
            <div className="stats-grid">
              <div className="stat">
                <div className="stat-value">{assetCount}</div>
                <div className="stat-label">Assets</div>
              </div>
              <div className="stat">
                <div className="stat-value">{stockItemCount}</div>
                <div className="stat-label">Stock Items</div>
              </div>
              <div className="stat">
                <div className="stat-value">{lowStockItems.length}</div>
                <div className="stat-label">Low Stock</div>
              </div>
            </div>
          </div>
        </div>

        <div className="quick-actions">
          <h3>Quick Actions</h3>
          <div className="actions-grid">
            <button className="action-btn" onClick={() => navigate('/inventory/assets')}>
              <Package size={24} />
              <span>Assets</span>
            </button>
            <button className="action-btn" onClick={() => navigate('/inventory/stock-items')}>
              <Archive size={24} />
              <span>Stock Items</span>
            </button>
            <button className="action-btn" onClick={() => navigate('/inventory/categories')}>
              <Archive size={24} />
              <span>Categories</span>
            </button>
          </div>
        </div>
        </>
        )}
      </div>
    </Layout>
  );
}

export default StorekeeperPortalPage;
